/**
 * ResponseFormatter
 *
 * Renders a bot message body based on the response format:
 *   - 'table' with data  ->  MUI Table with auto-detected columns
 *   - 'list'             ->  bullet list
 *   - 'text' (default)   ->  plain Typography
 *
 * Also renders optional suggestion chips below the message content.
 */

import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useTheme } from '@mui/material/styles'
import type { CopilotMessage } from './CopilotProvider'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ResponseFormatterProps {
  message: CopilotMessage
  onSuggestionClick?: (text: string) => void
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

const ResponseTable: React.FC<{ data: Record<string, unknown>[] }> = ({ data }) => {
  const theme = useTheme()
  if (data.length === 0) return null
  const columns = Object.keys(data[0])

  return (
    <TableContainer
      sx={{
        mt: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        maxHeight: 220,
        overflow: 'auto',
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map(col => (
              <TableCell
                key={col}
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => (
            <TableRow key={idx}>
              {columns.map(col => (
                <TableCell
                  key={col}
                  sx={{
                    color: 'text.primary',
                    fontSize: '0.8rem',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {String(row[col] ?? '')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const renderList = (text: string) => {
  const items = text.split('\n').filter(Boolean)
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {items.map((item, idx) => (
        <Typography
          key={idx}
          component="li"
          variant="body2"
          sx={{ color: 'text.primary', mb: 0.25 }}
        >
          {item.replace(/^[-*]\s*/, '')}
        </Typography>
      ))}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ResponseFormatter: React.FC<ResponseFormatterProps> = ({
  message,
  onSuggestionClick,
}) => {
  const { text, data, format, suggestions } = message

  return (
    <Box>
      {/* Main content */}
      {format === 'table' && data && data.length > 0 ? (
        <>
          {text && (
            <Typography variant="body2" sx={{ color: 'text.primary', mb: 0.5 }}>
              {text}
            </Typography>
          )}
          <ResponseTable data={data} />
        </>
      ) : format === 'list' ? (
        renderList(text)
      ) : (
        <Typography
          variant="body2"
          sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}
        >
          {text}
        </Typography>
      )}

      {/* Suggestion chips */}
      {suggestions && suggestions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {suggestions.map((s, idx) => (
            <Chip
              key={idx}
              label={s}
              size="small"
              variant="outlined"
              onClick={() => onSuggestionClick?.(s)}
              sx={{
                color: 'primary.light',
                borderColor: 'divider',
                fontSize: '0.75rem',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: 'primary.main',
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default ResponseFormatter
